import type {
  ImaAdEvent,
  ImaAdsManager,
  ImaAdsManagerLoadedEvent,
  ImaAdsRequest,
  ImaAdsRenderingSettings,
  ImaSdk,
} from "../types/ima";

export interface ImaControllerCallbacks {
  onManagerLoaded(): void;
  onContentPauseRequested(): void;
  onContentResumeRequested(): void;
  onLoaded(event: ImaAdEvent): void;
  onStarted(): void;
  onPaused(): void;
  onResumed(): void;
  onSkipped(): void;
  onError(error: unknown): void;
}

export interface ImaRequestOptions {
  adTagUrl: string;
  contentTitle: string;
  pageUrl: string;
  autoPlay: boolean;
  muted: boolean;
}

function getSize(element: HTMLElement): { width: number; height: number } {
  return {
    width: element.clientWidth || 640,
    height: element.clientHeight || 360,
  };
}

export class ImaController {
  private readonly adDisplayContainer: { initialize(): void; destroy?(): void };
  private readonly adsLoader: {
    addEventListener<TEvent>(
      type: string,
      listener: (event: TEvent) => void,
      capture?: boolean,
    ): void;
    destroy?(): void;
    requestAds(request: ImaAdsRequest): void;
  };
  private readonly callbacks: ImaControllerCallbacks;
  private readonly sdk: ImaSdk;
  private readonly video: HTMLVideoElement;
  private adsManager: ImaAdsManager | null = null;
  private initialized = false;
  private destroyed = false;
  private volume = 1;
  private requestInFlight = false;

  constructor(
    sdk: ImaSdk,
    video: HTMLVideoElement,
    displayContainer: HTMLElement,
    callbacks: ImaControllerCallbacks,
  ) {
    this.sdk = sdk;
    this.video = video;
    this.callbacks = callbacks;
    this.adDisplayContainer = new sdk.AdDisplayContainer(
      displayContainer,
      video,
    );
    this.adsLoader = new sdk.AdsLoader(this.adDisplayContainer);

    this.adsLoader.addEventListener<ImaAdsManagerLoadedEvent>(
      sdk.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      (event) => {
        this.settleRequest();
        this.onAdsManagerLoaded(event);
      },
      false,
    );
    this.adsLoader.addEventListener(
      sdk.AdErrorEvent.Type.AD_ERROR,
      (error) => {
        this.settleRequest();
        this.callbacks.onError(error);
      },
      false,
    );
  }

  start(): void {
    if (this.destroyed || this.initialized) return;
    this.video.load();
    this.adDisplayContainer.initialize();
    this.initialized = true;
  }

  request(options: ImaRequestOptions): void {
    if (this.destroyed) return;

    const request = new this.sdk.AdsRequest();
    const { width, height } = getSize(this.video);

    request.adTagUrl = options.adTagUrl;
    request.contentTitle = options.contentTitle;
    request.pageUrl = options.pageUrl;
    request.forceNonLinearFullSlot = true;
    request.linearAdSlotWidth = width;
    request.linearAdSlotHeight = height;
    request.nonLinearAdSlotWidth = width;
    request.nonLinearAdSlotHeight = height;
    request.setAdWillAutoPlay(options.autoPlay);
    request.setAdWillPlayMuted(options.muted);
    this.requestInFlight = true;
    this.adsLoader.requestAds(request);
  }

  setVolume(volume: number): void {
    this.volume = volume;
    this.adsManager?.setVolume(volume);
  }

  pause(): void {
    this.adsManager?.pause();
  }

  resume(): void {
    this.adsManager?.resume();
  }

  resize(viewMode: unknown): void {
    if (!this.adsManager || this.destroyed) return;
    const { width, height } = getSize(this.video);
    this.adsManager.resize(width, height, viewMode);
  }

  destroyManager(): void {
    this.adsManager?.destroy();
    this.adsManager = null;
  }

  private destroyLoader(): void {
    this.adsLoader.destroy?.();
    this.adDisplayContainer.destroy?.();
  }

  private settleRequest(): void {
    if (!this.requestInFlight) return;
    this.requestInFlight = false;
    if (this.destroyed) this.destroyLoader();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.destroyManager();
    // Destroying the loader mid-request makes IMA reject an internal promise,
    // surfacing the pending ad error as an unhandled rejection; defer teardown
    // until the in-flight request settles.
    if (this.requestInFlight) return;
    this.destroyLoader();
  }

  private onAdsManagerLoaded(event: ImaAdsManagerLoadedEvent): void {
    if (this.destroyed) return;

    this.adsManager?.destroy();
    this.adsManager = null;
    const settings: ImaAdsRenderingSettings =
      new this.sdk.AdsRenderingSettings();
    settings.restoreCustomPlaybackStateOnAdBreakComplete = true;
    const manager = event.getAdsManager(this.video, settings);
    this.adsManager = manager;
    this.callbacks.onManagerLoaded();

    manager.addEventListener(
      this.sdk.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
      () => this.callbacks.onContentPauseRequested(),
    );
    manager.addEventListener(
      this.sdk.AdEvent.Type.CONTENT_RESUME_REQUESTED,
      () => this.callbacks.onContentResumeRequested(),
    );
    manager.addEventListener<ImaAdEvent>(
      this.sdk.AdEvent.Type.LOADED,
      (adEvent) => this.callbacks.onLoaded(adEvent),
    );
    manager.addEventListener(
      this.sdk.AdEvent.Type.STARTED,
      () => this.callbacks.onStarted(),
    );
    manager.addEventListener(
      this.sdk.AdEvent.Type.PAUSED,
      () => this.callbacks.onPaused(),
    );
    manager.addEventListener(
      this.sdk.AdEvent.Type.RESUMED,
      () => this.callbacks.onResumed(),
    );
    manager.addEventListener(
      this.sdk.AdEvent.Type.SKIPPED,
      () => this.callbacks.onSkipped(),
    );
    manager.addEventListener(
      this.sdk.AdErrorEvent.Type.AD_ERROR,
      (error) => this.callbacks.onError(error),
    );

    try {
      const { width, height } = getSize(this.video);
      manager.init(width, height, this.sdk.ViewMode.NORMAL);
      manager.setVolume(this.volume);
      manager.start();
    } catch (error) {
      this.callbacks.onError(error);
    }
  }
}
